"use client"

import { motion } from "framer-motion"
import { Monitor, X } from "lucide-react"
import type { PromptFile, SelectedTemplate } from "./types"
import type { KnowledgeBaseFile } from "@/app/store/api/knowledgeBaseApi"
import { formatFileSize, getFileVisuals } from "./file-utils"

interface AttachmentChipsProps {
    logoFile: File | null
    onLogoChange: (file: File | null) => void
    selectedTemplate?: SelectedTemplate
    onRemoveTemplate?: () => void
    files: PromptFile[]
    onRemoveFile: (index: number) => void
}

export function AttachmentChips({
    logoFile,
    onLogoChange,
    selectedTemplate,
    onRemoveTemplate,
    files,
    onRemoveFile,
}: AttachmentChipsProps) {
    if (!selectedTemplate && files.length === 0 && !logoFile) return null

    return (
        <div className="flex flex-wrap gap-2">
            {/* Logo chip */}
            {logoFile && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2 }}
                    className="relative flex items-center bg-white border border-purple-200 rounded-lg p-1.5 pl-2 gap-2 text-xs group"
                >
                    <div className="w-7 h-7 rounded overflow-hidden flex-shrink-0 border border-slate-200 bg-slate-50">
                        <img
                            src={URL.createObjectURL(logoFile)}
                            alt="Logo"
                            className="w-full h-full object-contain"
                        />
                    </div>
                    <div className="flex-1 min-w-0 overflow-hidden">
                        <span className="text-xs font-medium text-slate-700 truncate block">
                            {logoFile.name.length > 20 ? logoFile.name.substring(0, 17) + "..." : logoFile.name}
                        </span>
                        <span className="text-[10px] text-purple-500 block font-medium">Logo</span>
                    </div>
                    <button
                        onClick={() => onLogoChange(null)}
                        type="button"
                        className="ml-1 p-0.5 text-slate-400 hover:text-red-600 transition-colors cursor-pointer"
                    >
                        <X className="w-3 h-3" />
                    </button>
                </motion.div>
            )}

            {/* Selected template chip */}
            {selectedTemplate && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2 }}
                    className="relative flex items-center bg-white border border-blue-200 rounded-lg p-1.5 pl-2 gap-2 text-xs group"
                >
                    {selectedTemplate.thumbnail ? (
                        <img
                            src={selectedTemplate.thumbnail}
                            alt={selectedTemplate.name}
                            className="w-8 h-5 rounded object-cover border border-slate-200 flex-shrink-0"
                        />
                    ) : (
                        <div className="bg-blue-500 p-1 rounded flex-shrink-0">
                            <Monitor className="w-3.5 h-3.5 text-white" />
                        </div>
                    )}
                    <div className="flex-1 min-w-0 overflow-hidden">
                        <span className="text-xs font-medium text-slate-700 truncate block">
                            {selectedTemplate.name.length > 22 ? selectedTemplate.name.substring(0, 19) + "..." : selectedTemplate.name}
                        </span>
                        <span className="text-[10px] text-blue-500 block font-medium">Template</span>
                    </div>
                    <button
                        onClick={onRemoveTemplate}
                        type="button"
                        className="ml-1 p-0.5 text-slate-400 hover:text-red-600 transition-colors cursor-pointer"
                    >
                        <X className="w-3 h-3" />
                    </button>
                </motion.div>
            )}

            {/* File chips */}
            {files.map((file, index) => {
                const { icon, bg } = getFileVisuals(file)
                const fileName = (file as File).name || (file as KnowledgeBaseFile).fileName || "Unknown"
                const isRemote = !('size' in file)

                return (
                    <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.2 }}
                        className="relative flex items-center bg-white border border-slate-200 rounded-lg p-1.5 pl-2 gap-2 text-xs group"
                    >
                        <div className={`${bg} p-1 rounded flex-shrink-0`}>
                            {icon}
                        </div>
                        <div className="flex-1 min-w-0 overflow-hidden">
                            <span className="text-xs font-medium text-slate-700 truncate block">
                                {fileName.length > 20 ? fileName.substring(0, 17) + "..." : fileName}
                            </span>
                            <span className="text-[10px] text-slate-500 block">
                                {isRemote ? "Knowledge Base" : formatFileSize((file as File).size)}
                            </span>
                        </div>
                        <button
                            onClick={() => onRemoveFile(index)}
                            type="button"
                            className="ml-1 p-0.5 text-slate-400 hover:text-red-600 transition-colors"
                        >
                            <X className="w-3 h-3" />
                        </button>
                    </motion.div>
                )
            })}
        </div>
    )
}
